import React, { useState } from 'react';
import PostalCodeLookup from '../UI/PostalCodeLookup';
import Modal from '../UI/Modal';
import toast from '../../utils/toast';
import { formatDateForInput, parseLocalDate, toLocalISODate } from '../../utils/dateUtils';
import DatePickerField from '../UI/DatePickerField';
import { 
  Plus, 
  Edit3, 
  X, 
  Building, 
  Calendar, 
  User, 
  MapPin, 
  CheckCircle,
  AlertCircle,
  Save,
  Ban
} from 'lucide-react';

const SiteForm = ({ project, onClose, onSave, isAdmin = false, isModal = true }) => {
  const [formData, setFormData] = useState({
    project_code: '',
    project_name: '',
    project_type: 'other',
    project_address: '',
    project_postal_code: '',
    unit_number: '',
    project_latitude: '',
    project_longitude: '',
    project_geofence_radius: 150,
    project_status: 'active',
    project_start_date: '',
    project_end_date: '',
    project_manager_staff_id: '',
    project_description: '',
    project_client_name: '',
    project_requires_checkin: true,
    // Format dates for HTML5 date inputs (YYYY-MM-DD format)
    ...(project ? {
      ...project,
      project_start_date: formatDateForInput(project.project_start_date),
      project_end_date: formatDateForInput(project.project_end_date),
    } : {})
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});



  // Handle postal code lookup success (for compatibility with PostalCodeLookup component)
  const handleAddressFound = (geocodedData) => {
    setFormData({
      ...formData,
      project_address: geocodedData.address,
      project_postal_code: geocodedData.postal_code || '',
      project_latitude: geocodedData.latitude,
      project_longitude: geocodedData.longitude
    });

    // Clear any previous address-related errors
    setErrors({
      ...errors,
      project_address: '',
      project_latitude: '',
      project_longitude: ''
    });

    console.log('📍 Auto-filled project address:', geocodedData);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Basic validation
      const newErrors = {};
      
      if (!formData.project_code.trim()) {
        newErrors.project_code = 'Project code is required';
      }
      
      if (!formData.project_name.trim()) {
        newErrors.project_name = 'Project name is required';
      }

      // Validate coordinates if provided
      if (formData.project_latitude && formData.project_longitude) {
        const lat = parseFloat(formData.project_latitude);
        const lng = parseFloat(formData.project_longitude);
        
        if (isNaN(lat) || isNaN(lng)) {
          newErrors.coordinates = 'Invalid coordinate format';
        } else if (lat < 1.1 || lat > 1.5 || lng < 103.6 || lng > 104.1) {
          newErrors.coordinates = 'Coordinates should be within Singapore region';
        }
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        toast.error('Please fix the form errors');
        return;
      }

      // Submit the form
      await onSave(formData);
      
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('❌ Failed to save project');
    } finally {
      setIsSubmitting(false);
    }
  };

  const projectTypes = [
    { value: 'hospital', label: 'Hospital/Medical' },
    { value: 'polyclinic', label: 'Polyclinic' },
    { value: 'office', label: 'Office Building' },
    { value: 'factory', label: 'Factory/Industrial' },
    { value: 'residential', label: 'Residential' },
    { value: 'infrastructure', label: 'Infrastructure' },
    { value: 'government', label: 'Government Building' },
    { value: 'education', label: 'Educational Institution' },
    { value: 'commercial', label: 'Commercial Building' },
    { value: 'other', label: 'Other' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'on_hold', label: 'On Hold' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'planning', label: 'Planning Stage' }
  ];

  const formContent = (
    <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="form-group">
              <label className="form-label">
                <Building className="w-4 h-4 inline mr-2" />
                Project Code *
              </label>
              <input
                type="text"
                value={formData.project_code}
                onChange={(e) => setFormData({ ...formData, project_code: e.target.value })}
                placeholder="e.g., P2024-001, OFFICE-PRIMARY"
                className={`${isAdmin ? 'input-admin' : 'input'} ${errors.project_code ? 'input-error' : ''}`}
                disabled={!!project} // Disable when editing
              />
              {errors.project_code && (
                <p className="form-error">{errors.project_code}</p>
              )}
              {project && (
                <p className="form-help">Project code cannot be changed when editing</p>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                Project Name *
              </label>
              <input
                type="text"
                value={formData.project_name}
                onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
                placeholder="e.g., Marina Bay Office Development"
                className={`${isAdmin ? 'input-admin' : 'input'} ${errors.project_name ? 'input-error' : ''}`}
              />
              {errors.project_name && (
                <p className="form-error">{errors.project_name}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="form-group">
              <label className="form-label">
                Project Type
              </label>
              <select
                value={formData.project_type}
                onChange={(e) => setFormData({ ...formData, project_type: e.target.value })}
                className={isAdmin ? 'select-admin' : 'select'}
              >
                {projectTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">
                Status
              </label>
              <select
                value={formData.project_status}
                onChange={(e) => setFormData({ ...formData, project_status: e.target.value })}
                className={isAdmin ? 'select-admin' : 'select'}
              >
                {statusOptions.map(status => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <User className="w-4 h-4 inline mr-2" />
              Client Name
            </label>
            <input
              type="text"
              value={formData.project_client_name}
              onChange={(e) => setFormData({ ...formData, project_client_name: e.target.value })}
              placeholder="e.g., ABC Development Pte Ltd"
              className={isAdmin ? 'input-admin' : 'input'}
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              Description
            </label>
            <textarea
              value={formData.project_description}
              onChange={(e) => setFormData({ ...formData, project_description: e.target.value })}
              placeholder="Detailed description of the project..."
              rows={3}
              className={isAdmin ? 'textarea-admin' : 'textarea'}
            />
          </div>

          {/* Location & Address Section */}
          <div className="border-t pt-6">
            <h3 className={`text-lg font-medium text-gray-900 mb-4 flex items-center`}>
              <MapPin className={`w-5 h-5 mr-2 ${isAdmin ? 'text-red-600' : 'text-blue-600'}`} />
              Location & Address
            </h3>
            
            {/* Enhanced Postal Code Lookup Component */}
            <div className="mb-6">
              <PostalCodeLookup
                value={formData.project_postal_code}
                onAddressFound={handleAddressFound}
                onPostalCodeChange={(newPostalCode) => {
                  setFormData({ ...formData, project_postal_code: newPostalCode });
                }}
                onError={(error) => toast.error(error)}
                placeholder="Enter postal code to auto-fill address (e.g., 018989)"
                buttonText="Look Up Address"
                isAdmin={isAdmin}
              />
              <p className="form-help mt-2">
                💡 <strong>Tip:</strong> Use postal code lookup to quickly fill address and coordinates, then add unit number if needed
              </p>
            </div>

            {/* Manual Address Input */}
            <div className="space-y-4">
              <div className="form-group">
                <label className="form-label">
                  Project Address
                </label>
                <input
                  type="text"
                  value={formData.project_address}
                  onChange={(e) => setFormData({ ...formData, project_address: e.target.value })}
                  placeholder="Full address of the project site (auto-filled from postal code lookup)"
                  className={`${isAdmin ? 'input-admin' : 'input'} ${errors.project_address ? 'input-error' : ''}`}
                />
                {errors.project_address && (
                  <p className="form-error">{errors.project_address}</p>
                )}
              </div>

              {/* Unit Number Field */}
              <div className="form-group">
                <label className="form-label">
                  Unit Number
                </label>
                <input
                  type="text"
                  value={formData.unit_number || ''}
                  onChange={(e) => setFormData({ ...formData, unit_number: e.target.value })}
                  placeholder="e.g., #12-34, Unit 5, Level 3"
                  className={isAdmin ? 'input-admin' : 'input'}
                />
                <p className="form-help">
                  Optional: Floor/unit number for complete address
                </p>
              </div>

              {/* GPS Coordinates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">
                    🌐 Latitude
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={formData.project_latitude}
                    onChange={(e) => setFormData({ ...formData, project_latitude: e.target.value })}
                    placeholder="1.284038 (auto-filled)"
                    className={isAdmin ? 'input-admin' : 'input'}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    🌐 Longitude
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={formData.project_longitude}
                    onChange={(e) => setFormData({ ...formData, project_longitude: e.target.value })}
                    placeholder="103.851287 (auto-filled)"
                    className={isAdmin ? 'input-admin' : 'input'}
                  />
                </div>
              </div>
              
              {errors.coordinates && (
                <p className="form-error">{errors.coordinates}</p>
              )}

              {/* Coordinate Display */}
              {formData.project_latitude && formData.project_longitude && (
                <div className="alert alert-info">
                  <MapPin className="alert-icon" />
                  <div>
                    <p className="text-sm">
                      <strong>Coordinates:</strong> {parseFloat(formData.project_latitude).toFixed(6)}, {parseFloat(formData.project_longitude).toFixed(6)}
                    </p>
                    <p className="text-xs mt-1">
                      Geofence radius: {formData.project_geofence_radius} meters
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Project Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="form-group">
              <label className="form-label">
                Geofence Radius (meters)
              </label>
              <input
                type="number"
                min="50"
                max="500"
                value={formData.project_geofence_radius}
                onChange={(e) => setFormData({ ...formData, project_geofence_radius: parseInt(e.target.value) || 150 })}
                className={isAdmin ? 'input-admin' : 'input'}
              />
              <p className="form-help">
                Detection radius for automatic check-in (50-500 meters)
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="requires_checkin"
                  checked={formData.project_requires_checkin}
                  onChange={(e) => setFormData({ ...formData, project_requires_checkin: e.target.checked })}
                  className={`h-4 w-4 border-gray-300 rounded ${isAdmin ? 'text-red-600 focus-visible:ring-red-500' : 'text-blue-600 focus-visible:ring-blue-500'}`}
                />
                <label htmlFor="requires_checkin" className="ml-2 text-sm text-gray-700 flex items-center">
                  <CheckCircle className={`w-4 h-4 mr-1 ${isAdmin ? 'text-red-600' : 'text-blue-600'}`} />
                  Requires staff check-in
                </label>
              </div>
            </div>
          </div>

          {/* Project Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="form-group">
              <label className="form-label">
                <Calendar className="w-4 h-4 inline mr-2" />
                Start Date
              </label>
              <DatePickerField
                theme="employee"
                selected={parseLocalDate(formData.project_start_date)}
                onChange={(date) => setFormData({ ...formData, project_start_date: date ? toLocalISODate(date) : '' })}
                placeholderText="Select start date"
                isClearable
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                <Calendar className="w-4 h-4 inline mr-2" />
                End Date
              </label>
              <DatePickerField
                theme="employee"
                selected={parseLocalDate(formData.project_end_date)}
                onChange={(date) => setFormData({ ...formData, project_end_date: date ? toLocalISODate(date) : '' })}
                placeholderText="Select end date"
                isClearable
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className={`btn ${isAdmin ? 'btn-admin-secondary' : 'btn-secondary'}`}
            >
              <Ban className="w-4 h-4 mr-2" />
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`btn ${isAdmin ? 'btn-admin-primary' : 'btn-primary'}`}
            >
              {isSubmitting ? (
                <>
                  <div className={`loading-spinner loading-spinner-sm mr-2 ${isAdmin ? 'loading-spinner-admin' : ''}`} />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {project ? 'Update Project' : 'Create Project'}
                </>
              )}
            </button>
          </div>
        </form>
  );

  // If isModal is false, just return the form content without modal wrapper
  if (!isModal) {
    return formContent;
  }

  // Otherwise, return the full modal
  return (
    <Modal
      isOpen
      onClose={onClose}
      size="xl"
      theme={isAdmin ? 'admin' : 'employee'}
      className="max-w-4xl"
      contentClassName="!p-0"
      customHeader={(
        <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4 rounded-t-2xl">
          <div className="flex justify-between items-center gap-2">
            <div className="min-w-0 flex-1">
              <h2 className="text-base sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2 flex items-center min-w-0">
                {project ? (
                  <>
                    <Edit3 className={`w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 flex-shrink-0 ${isAdmin ? 'text-red-600' : 'text-blue-600'}`} />
                    <span className="truncate">Edit Project</span>
                  </>
                ) : (
                  <>
                    <Plus className={`w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 flex-shrink-0 ${isAdmin ? 'text-red-600' : 'text-blue-600'}`} />
                    <span className="truncate">Create New Project</span>
                  </>
                )}
              </h2>
              <p className="text-xs sm:text-sm text-gray-600">
                {project ? 'Update project details and settings' : 'Create a new project with location and settings'}
              </p>
            </div>
            <button
              onClick={onClose}
              className={`${isAdmin ? 'icon-button-admin' : 'icon-button'} flex-shrink-0`}
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    >
        {formContent}
    </Modal>
  );
};

export default SiteForm; 